import { useEffect, useRef, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

// Collaboration Phase 1: one Supabase Realtime channel per project.
// - presence: who has the deck open, and which slide each person is on
// - broadcast 'deck_updated': fired after a successful save so other viewers
//   can refresh (payload carries {rev, by}; content travels via the DB, not
//   the channel, to stay far below Realtime message-size limits)
export function useProjectPresence({ projectId, userId, onRemoteUpdate }) {
  const [peers, setPeers] = useState([]); // [{user_id, name, editingSlide}]
  const channelRef = useRef(null);
  const nameRef = useRef('Teammate');
  const editingRef = useRef(null);
  const onRemoteRef = useRef(onRemoteUpdate);
  onRemoteRef.current = onRemoteUpdate;

  useEffect(() => {
    if (!projectId || !userId) return undefined;
    let cancelled = false;

    // Resolve a display name once; presence payloads carry it so peers can
    // render "Tala is on slide 3" without extra lookups.
    supabase.auth.getUser().then(({ data }) => {
      const u = data?.user;
      const name = u?.user_metadata?.display_name || (u?.email || '').split('@')[0] || 'Teammate';
      nameRef.current = name;
      if (!cancelled && channelRef.current) {
        try { channelRef.current.track({ name, editingSlide: editingRef.current }); } catch (_) { /* not subscribed yet */ }
      }
    }).catch(() => {});

    const channel = supabase.channel(`project:${projectId}`, {
      config: { presence: { key: userId }, broadcast: { self: false } },
    });
    channelRef.current = channel;

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        const others = [];
        Object.keys(state).forEach((key) => {
          if (key === userId) return;
          const metas = state[key];
          if (metas && metas.length) others.push({ user_id: key, ...metas[metas.length - 1] });
        });
        setPeers(others);
      })
      .on('broadcast', { event: 'deck_updated' }, (msg) => {
        onRemoteRef.current?.(msg?.payload || {});
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          try { channel.track({ name: nameRef.current, editingSlide: editingRef.current }); } catch (_) { /* best-effort */ }
        }
      });

    return () => {
      cancelled = true;
      try { supabase.removeChannel(channel); } catch (_) { /* already gone */ }
      channelRef.current = null;
      setPeers([]);
    };
  }, [projectId, userId]);

  // Which slide this user currently has open (null when not reviewing).
  const setEditingSlide = useCallback((idx) => {
    editingRef.current = idx;
    try { channelRef.current?.track({ name: nameRef.current, editingSlide: idx }); } catch (_) { /* not subscribed */ }
  }, []);

  const broadcastDeckUpdated = useCallback((rev) => {
    try {
      channelRef.current?.send({
        type: 'broadcast',
        event: 'deck_updated',
        payload: { kind: 'deck', rev: rev ?? null, by: nameRef.current },
      });
    } catch (_) { /* channel down — peers refresh on next load */ }
  }, []);

  // Phase 2: a single slide was saved atomically — peers can hot-swap just
  // that slide (content travels via a DB refetch, not the channel).
  const broadcastSlideUpdated = useCallback((index, rev) => {
    try {
      channelRef.current?.send({
        type: 'broadcast',
        event: 'deck_updated',
        payload: { kind: 'slide', index, rev: rev ?? null, by: nameRef.current },
      });
    } catch (_) { /* channel down — peers refresh on next load */ }
  }, []);

  return { peers, setEditingSlide, broadcastDeckUpdated, broadcastSlideUpdated };
}
